// src/screens/energy-reset/DashboardScreen.tsx
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";

import StreakBar from "../../components/energy-reset/StreakBar";
import XpRing from "../../components/energy-reset/XpRing";
import EveningSurvey from "../../components/energy-reset/EveningSurvey";
import NudgeBanner from "../../components/energy-reset/NudgeBanner";
import Countdown from "../../components/common/Countdown";
import Modal from "../../components/Modal";
import ArtifactRail from "../../components/energy-reset/ArtifactRail";
import ArtifactModal from "../../components/energy-reset/ArtifactModal";
import BadgeStrip from "../../components/energy-reset/BadgeStrip";
import BadgeZoomOverlay from "../../components/energy-reset/BadgeZoomOverlay";

import { dayProgressPercent, isBetweenTZ, isAfterTZ } from "../../utils/time";
import { logEvent } from "../../utils/events";
import { getCourseSettingsByCourseId, DEFAULTS as SETTINGS_DEFAULTS } from "../../utils/energy-reset/settings";
import type { CourseSettings } from "../../utils/energy-reset/settings";
import { API_BASE } from "../../utils/api";

/* === Гейт (Фаза 2): снапшот/прогресс/контент за серверными эндпоинтами === */
import { getSnapshot, findEnrollment, type AccessSnapshot, type SnapshotEnrollment } from "../../api/snapshot";
import { getDays, completeDay, type ProgressDays } from "../../api/progress";
import { getContentDay, mediaUrl, type DayContent, type DayMedia, type ContentDayResponse } from "../../api/content";
import { getCourses } from "../../api/courses";
import {
  enqueue as enqueueOfflineCompletion,
  getPending,
  getRejected,
  clearRejectedForCourse,
  type QueueEntry,
  type RejectedEntry,
} from "../../utils/queue";
import { computeLocalDate, computeStreak } from "../../utils/localDate";
import { resolveToday, type TodayResolution } from "../../utils/schedule";

/* === XP/Badges === */
import { nextTargetFor, qualifyingBadgeFrom } from "../../utils/xp";
import {
  loadBadgesCatalog,
  loadAwarded,
  type Badge,
  type BadgeAward,
} from "../../api/badges";

/* NEW: эффект полёта */
import SparksFly from "../../components/wallet/SparksFly";
import { fetchArtifacts, type Artifact } from "../../api/artifacts";

type Pane = "dashboard" | "practice";

type ArtifactMode = "award" | "preview" | "locked";
type LockedReason = "future" | "today" | "missed" | null;

/* === Единое состояние слоя данных гейта === */
type DashState = {
  snapshot: AccessSnapshot | null;
  days: { list: { day_number: number; local_date: string }[]; todayCompleted: boolean; todayYmd: string };
  badges: Badge[];
  awards: BadgeAward[];
  content: DayContent | null;
  media: DayMedia;
  loading: boolean;
  error: string | null;
};

type DashAction =
  | { type: "INIT_START" }
  | {
      type: "INIT_OK";
      snapshot: AccessSnapshot;
      days: ProgressDays;
      badges: Badge[];
      awards: BadgeAward[];
      content: DayContent | null;
      media: DayMedia;
    }
  | { type: "INIT_FAIL"; error: string }
  | { type: "SNAPSHOT_SET"; snapshot: AccessSnapshot }
  | { type: "DAYS_SET"; days: ProgressDays }
  | { type: "AWARDS_SET"; awards: BadgeAward[] }
  | { type: "CONTENT_SET"; content: DayContent | null; media: DayMedia };

const EMPTY_MEDIA: DayMedia = { audio_url: null, video_url: null };

const initialDashState: DashState = {
  snapshot: null,
  days: { list: [], todayCompleted: false, todayYmd: "" },
  // M5-STEP3B: раньше здесь лежал DEFAULT_BADGES — дефолт появлялся ДО первого
  // запроса, решение владельца «без индикатора» из-за этого не срабатывало
  // (человек видел дефолт мгновенно). Фолбэк подставляется только после ответа
  // сервера, в loadBadgesCatalog(), для легитимного "каталог пуст".
  badges: [],
  awards: [],
  content: null,
  media: EMPTY_MEDIA,
  loading: true,
  error: null,
};

function dashReducer(state: DashState, action: DashAction): DashState {
  switch (action.type) {
    case "INIT_START":
      return { ...state, loading: true, error: null };
    case "INIT_OK":
      return {
        ...state,
        snapshot: action.snapshot,
        days: {
          list: action.days.days,
          todayCompleted: action.days.today_completed,
          todayYmd: action.days.today_ymd,
        },
        badges: action.badges,
        awards: action.awards,
        content: action.content,
        media: action.media,
        loading: false,
        error: null,
      };
    case "INIT_FAIL":
      return { ...state, loading: false, error: action.error };
    case "SNAPSHOT_SET":
      return { ...state, snapshot: action.snapshot };
    case "DAYS_SET":
      return {
        ...state,
        days: {
          list: action.days.days,
          todayCompleted: action.days.today_completed,
          todayYmd: action.days.today_ymd,
        },
      };
    case "AWARDS_SET":
      return { ...state, awards: action.awards };
    case "CONTENT_SET":
      return { ...state, content: action.content, media: action.media };
    default:
      return state;
  }
}

// QUEUE-UI-B, Шаг 6: текст плашки отклонённых записей — по-человечески,
// по причине из res.error (queue.ts, addRejected) — набор строк см. в
// комментарии flushQueue (queue.ts): полный список 400/403/404-причин
// сервера, здесь — только те, что задание просит различать текстом;
// остальные (включая max_attempts_exceeded:*) идут в общую формулировку.
function rejectionMessage(dayNumber: number, reason: string): string {
  switch (reason) {
    case "grace_expired":
      return `День ${dayNumber} не засчитан: прошло больше трёх дней без связи`;
    case "course_completed":
      return `День ${dayNumber} не засчитан: курс уже завершён`;
    case "not_active":
      return `День ${dayNumber} не засчитан: доступ к курсу закрыт`;
    case "enrollment_not_found":
      return `День ${dayNumber} не засчитан: курс не найден`;
    case "day_has_no_task":
      return `День ${dayNumber}: в этот день задания нет — отметка не засчитана`;
    default:
      return `День ${dayNumber} не засчитан. Попробуйте отметить его снова`;
  }
}

// OF-PKG1A (04.10.2026): какой день показать — из расписания снапшота
// (utils/schedule.ts); schedule null/undefined → current_day, как раньше.
// todayYmd — тем же computeLocalDate, что очередь и сервер.
function resolveEnrollmentToday(
  e: SnapshotEnrollment | null,
  tz: string,
  durationDays: number,
  now: Date = new Date()
): TodayResolution {
  return resolveToday({
    schedule: e?.schedule,
    currentDay: e?.current_day ?? 0,
    durationDays,
    todayYmd: computeLocalDate(now, tz, e?.morning_hour_effective, e?.morning_minute_effective),
  });
}

// День, за контентом которого идти: день без задания и «нужна связь» —
// контент не запрашивается (0, как предстарт).
function contentDayFor(res: TodayResolution): number {
  if (res.kind === "fallback") return res.day;
  if (res.kind === "day" && res.countable) return res.day;
  return 0;
}

export default function DashboardScreen() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const courseId = Number(id);
  const [pane, setPane] = useState<Pane>("dashboard");

  const [state, dispatch] = useReducer(dashReducer, initialDashState);

  const [settings, setSettings] = useState<CourseSettings | null>(null);
  // OF-PKG1A: refetchAll живёт и в замыканиях эффектов (queue:synced,
  // зависимость [courseId]) — там state settings из первого рендера (null),
  // поэтому duration_days для resolveToday читается через ref.
  const settingsRef = useRef<CourseSettings | null>(null);
  // OF-PKG1A, шаг 4: момент, по которому считается сегодняшняя дата на
  // экране. Двигается на переходе утра (Countdown.onReached), при возврате
  // вкладки и при появлении сети — день меняется и без сети (из schedule).
  const [nowTick, setNowTick] = useState(() => Date.now());
  const lastWakeRef = useRef(0);

  // праздничный баннер после бэкфилла — удалён вместе с бэкфиллом (см. NEXT_SESSION)
  // тост после ручного достижения нового рекорда
  const [recordToast, setRecordToast] = useState<number | null>(null);
  const [completeError, setCompleteError] = useState(false);
  // M5-QUEUE (20.09.2026): отметка не отправилась, но сохранена в очередь
  // (utils/queue.ts) и досошлётся сама — отдельно от истинного отказа
  // (completeError), у которого другой текст и который не самовосстановится.
  const [completeQueued, setCompleteQueued] = useState(false);

  // QUEUE-UI-A (21.09.2026): записи офлайн-очереди по этому курсу — источник
  // прогноза для doneToday/стрика и для значка «ждёт отправки» (ArtifactRail).
  const [pendingQueue, setPendingQueue] = useState<QueueEntry[]>([]);
  // QUEUE-UI-B: отклонённые записи очереди этого курса — плашка над
  // ArtifactRail, закрывается только человеком (Шаг 6 задания).
  const [rejectedEntries, setRejectedEntries] = useState<RejectedEntry[]>([]);

  const [badgeModal, setBadgeModal] = useState<{ code: string; label: string } | null>(null);

  // 🆕 Для мягкого подсвета плитки после выдачи
  const [badgesPulseKey, setBadgesPulseKey] = useState(0);

  // NEW: отдельная модалка «congrats» после закрытия практики
  const [congratsHtml, setCongratsHtml] = useState<string | null>(null);

  // Триггер для эффекта полёта
  const [flyKey, setFlyKey] = useState(0);

  // NEW: модалка артефакта
  const [artifactOpen, setArtifactOpen] = useState(false);
  const [artifactIndex, setArtifactIndex] = useState<number | null>(null);
  const [artifactMode, setArtifactMode] = useState<ArtifactMode>("preview");
  const [lockedReason, setLockedReason] = useState<LockedReason>(null);

  // 🆕 Состояние для FLIP-анимации бейджа
  const [zoomBadgeCode, setZoomBadgeCode] = useState<string | null>(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  const [courseTitle, setCourseTitle] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[] | null>(null);
  const [courseDesign, setCourseDesign] = useState<{
    course_background_color: string | null;
    dashboard_background_image_url: string | null;
  } | null>(null);
  // M5-STEP3B: true, когда каталог/награды не удалось спросить (не когда они
  // законно пусты) — блок достижений показывает текст вместо карточек, без
  // INIT_FAIL (украшение, не гейт) и без индикатора синхронизации (отложен).
  const [badgesUnavailable, setBadgesUnavailable] = useState(false);

  const cid = "";

  // ref на плитку «Мои достижения» — для scrollIntoView
  const badgesTileRef = useRef<HTMLDivElement | null>(null);

  // ✨ ВАЖНО: очередь на показ модалки бейджа (отложим до закрытия артефакта)
  const pendingBadgeRef = useRef<{ code: string; label: string } | null>(null);
  const lastBadgeRef = useRef<{ code: string; label: string } | null>(null);
  const pendingSparksRef = useRef(0);

  // утилиты ожиданий/скролла
  const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const scrollBadges = async () => {
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await wait(20);
    try {
      badgesTileRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {}
    await wait(450);
  };

  // загрузка: снапшот -> settings + дни + бейджи + контент дня (за гейтом)
  useEffect(() => {
    let alive = true;
    (async () => {
      dispatch({ type: "INIT_START" });
      try {
        const snapResult = await getSnapshot();
        // M5-STORE1: "cached" (офлайн, последний известный снимок) наравне с
        // "ok" — дашборд рисует день/искорки из кэша, INIT_FAIL не взводится.
        const snap =
          snapResult.status === "ok" || snapResult.status === "cached" ? snapResult.data : null;
        const e = snap ? findEnrollment(snap, courseId) : null;

        // OF-PKG1A: день для контента — resolveToday; ему нужен duration_days
        // (отличить «нужна связь» от конца курса), поэтому контент дня ждёт
        // только настройки, остальные запросы идут параллельно, как раньше.
        const settingsP = getCourseSettingsByCourseId(String(courseId));
        let d = 0;
        const contentP = settingsP.then((sr) => {
          const durationDays =
            sr.status === "ok" || sr.status === "cached"
              ? Number(sr.data.duration_days ?? 0)
              : sr.status === "empty"
                ? SETTINGS_DEFAULTS.duration_days
                : 0;
          d = contentDayFor(
            resolveEnrollmentToday(e, snap?.profile?.timezone ?? "Europe/Moscow", durationDays)
          );
          return d > 0
            ? getContentDay(courseId, d)
            // предстарт / день без задания / «нужна связь»: легитимно нет
            // контента, не трогается M5-STEP3A-FIX — status:"ok" добавлен
            // только для соответствия расширенному типу
            : Promise.resolve({ ok: true, status: "ok", day: null, media: EMPTY_MEDIA } as ContentDayResponse);
        });

        const [s, days, catalogResult, awardedResult, content, coursesRes, artifactsRes] = await Promise.all([
          settingsP,
          getDays(courseId),
          loadBadgesCatalog(courseId),
          loadAwarded(courseId),
          contentP,
          // M5-STORE3: fetch("/api/courses") сведён в getCourses() — украшение,
          // не гейт, INIT_FAIL не взводим; отказ и чтение из кэша логирует
          // сам модуль (courses.ts, префикс [courses]).
          getCourses(),
          // M5-STORE3: fetchArtifacts теперь различает "нет артефактов" от
          // "не спросили" и подмешивает кэш — форма как у остальных модулей.
          fetchArtifacts(courseId),
        ]);

        if (!alive) return;

        // M5-STEP3A: различаем причину неудачи снапшота — статус из захода 1
        // (getSnapshot()) читается здесь, не только распаковывается.
        if (snapResult.status === "unavailable") {
          console.error("[dashboard-init] snapshot unavailable");
          dispatch({ type: "INIT_FAIL", error: "Не удалось связаться с сервером. Проверьте соединение." });
          return;
        }
        if (!snap) {
          // status === "empty" — штатный случай (не куплено/не авторизован), не логируем
          dispatch({ type: "INIT_FAIL", error: "Ошибка загрузки" });
          return;
        }
        // SILENT-FIX (08.09.2026) + M5-STORE4 (18.09.2026): getCourseSettingsByCourseId()
        // отдаёт "unavailable" при сбое (сеть/сервер, и в IndexedDB тоже пусто) —
        // без этой проверки экран завис бы на «Загрузка…» навсегда (state.loading
        // уже false к этому моменту). INIT_FAIL — только на unavailable, как и
        // раньше на null. "empty" (404/400/403/401 — сервер ответил определённо)
        // подставляет те же DEFAULTS, что модуль сам отдаёт на легитимно пустой
        // data[] — тот же исход для экрана, разные причины в логе (settings.ts).
        if (s.status === "unavailable") {
          dispatch({ type: "INIT_FAIL", error: "Не удалось загрузить настройки курса" });
          return;
        }
        const settingsData = s.status === "empty" ? SETTINGS_DEFAULTS : s.data;
        // M5-STORE2 (16.09.2026): ветку INIT_FAIL для контента (M5-STEP3A) и
        // для прогресса убрали — обе построены на предположении, что без
        // текста дня/списка выполненных дней рисовать нечего. С появлением
        // кэша (content.ts/progress.ts) это перестало быть верным: снапшот и
        // настройки курса — единственное, что всё ещё держит экран (см. две
        // проверки выше). Отказ спросить остаётся различим в журнале, не
        // взводит INIT_FAIL и ничего не гасит на экране:
        // - контент недоступен → блок практики показывает свою ветку
        //   («Задание на этот день пока не заведено» — тот же текст, что и
        //   для легитимно пустого дня; PracticePane не различает причину);
        // - прогресс недоступен → календарь/артефакты рисуются по пустому
        //   списку дней, без ошибки (days уже приходит в форме {days:[],...}
        //   и на "empty", и на "unavailable" — см. progress.ts).
        if (d > 0 && content.status === "unavailable") {
          console.error("[dashboard-init] content day unavailable (no cache hit) — practice pane will show its empty branch");
        }
        if (days.status === "unavailable") {
          console.error("[dashboard-init] progress unavailable (no cache hit) — calendar renders without marks");
        }

        settingsRef.current = settingsData;
        setSettings(settingsData);
        // M5-STORE3: отказ каталога не взводит INIT_FAIL — название и дизайн
        // курса просто берутся из кэша, если он есть (getCourses() сам
        // логирует unavailable/чтение из кэша под префиксом [courses]).
        const courseItem =
          coursesRes.status === "ok" || coursesRes.status === "cached"
            ? coursesRes.data.find((c) => Number(c.id) === courseId) ?? null
            : null;
        setCourseDesign(courseItem?.design ?? null);
        setCourseTitle(courseItem?.title ?? null);

        // M5-STORE3: артефакты — украшение, не гейт; unavailable не роняет
        // экран, "cached" обрабатывается как успех.
        if (artifactsRes.status === "unavailable") {
          console.error("[dashboard-init] artifacts unavailable (no cache hit) — artifact rail renders without items");
        }
        const artifactsData =
          artifactsRes.status === "ok" || artifactsRes.status === "cached" ? artifactsRes.data : [];
        setArtifacts(artifactsData);

        // M5-STEP3B: отказ бейджей — украшение, не гейт (решение владельца,
        // уточнение 7.5) — INIT_FAIL не взводим. badgesUnavailable переключает
        // рендер блока достижений на текст ошибки вместо карточек.
        const badgesFailed = catalogResult.status === "unavailable" || awardedResult.status === "unavailable";
        if (badgesFailed) console.error("[dashboard-init] badges fetch failed");
        setBadgesUnavailable(badgesFailed);
        const badgesData = catalogResult.status === "unavailable" ? [] : catalogResult.data;
        const awardsData = awardedResult.status === "unavailable" ? [] : awardedResult.data;

        dispatch({
          type: "INIT_OK",
          snapshot: snap,
          days,
          badges: badgesData,
          awards: awardsData,
          content: content.day,
          media: content.media,
        });
      } catch (e: any) {
        console.error(e);
        if (alive) dispatch({ type: "INIT_FAIL", error: e?.message || "Ошибка загрузки" });
      }
    })();
    return () => {
      alive = false;
    };
  }, [courseId]);

  // QUEUE-UI-A: очередь этого курса — читается при монтировании и при каждом
  // queue:changed (постановка/отклонение в utils/queue.ts — доставка больше
  // не шлёт это событие, QUEUE-UI-B Шаг 2, см. queue.ts). Пустая очередь на
  // старте — тот же getPending([]), поведение экрана не меняется.
  // QUEUE-UI-B: тем же эффектом читаются отклонённые записи этого курса —
  // тот же источник события (постановка/доставка/отклонение затрагивают
  // оба хранилища через один и тот же queue:changed).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [list, rejected] = await Promise.all([getPending(courseId), getRejected()]);
      if (!alive) return;
      setPendingQueue(list);
      setRejectedEntries(rejected.filter((e) => String(e.courseId) === String(courseId)));
    };
    void load();
    const onChanged = () => {
      void load();
    };
    window.addEventListener("queue:changed", onChanged);
    return () => {
      alive = false;
      window.removeEventListener("queue:changed", onChanged);
    };
  }, [courseId]);

  // Производные значения из снапшота/гейта
  const enr = state.snapshot ? findEnrollment(state.snapshot, courseId) : null;
  // живой профиль, НЕ tz_at_start — закрывает известный баг приоритета таймера
  const tz = state.snapshot?.profile?.timezone ?? "Europe/Moscow";
  // OF-PKG1A: день на экране — из расписания снапшота на сегодняшнюю дату;
  // fallback (schedule null/undefined, [] и дата раньше окна) — current_day,
  // как раньше. «Нужна связь» (beyond) — заголовок по последнему дню окна.
  const today = resolveEnrollmentToday(enr, tz, Number(settings?.duration_days ?? 0), new Date(nowTick));
  const isRestDay = today.kind === "day" && !today.countable;
  const isBeyond = today.kind === "beyond";
  const lastScheduled = enr?.schedule?.length ? enr.schedule[enr.schedule.length - 1].day_number : 0;
  const dayN = today.kind === "beyond" ? lastScheduled : today.day;
  const xp = enr?.xp ?? 0;
  // Сумма балансов по всем кошелькам автора. Sparks начисляются ДЕЛЬТОЙ (M1c),
  // поэтому их разница до/после — достоверный признак начисления,
  // в отличие от xp, который сервер пересчитывает по всей истории.
  const sumSparks = (snap: AccessSnapshot | null) =>
    (snap?.sparks ?? []).reduce((acc, s) => acc + (Number(s?.balance) || 0), 0);

  // QUEUE-UI-A (21.09.2026): doneToday — истина, если так сказал сервер ИЛИ
  // текущий день лежит в очереди (запись поставлена, ещё не доставлена).
  // Пустая очередь → queuedToday всегда false → doneToday как было.
  const queuedToday = pendingQueue.some((e) => e.dayNumber === dayN);
  const doneToday = state.days.todayCompleted || queuedToday;

  // QUEUE-UI-A: при непустой очереди стрик/лучший — прогноз по объединению
  // серверных local_date (state.days.list) и localDate записей очереди
  // (сервер их ещё не видел, сегодня считается от текущего момента той же
  // computeLocalDate); при пустой очереди — ровно серверные значения, как
  // было (content-access, инвариант 2 — отрисовка не рубеж, показанное
  // офлайн — прогноз; он уходит по queue:synced ниже, эффект после refetchAll).
  const serverStreak = enr?.streak ?? 0;
  const serverBestStreak = enr?.best_streak ?? 0;
  let streakN = serverStreak;
  let bestStreak = serverBestStreak;
  if (pendingQueue.length > 0) {
    const serverDates = state.days.list.map((d) => d.local_date);
    const queueDates = pendingQueue.map((e) => e.localDate).filter((d): d is string => !!d);
    const todayYmd = computeLocalDate(new Date(), tz, enr?.morning_hour_effective, enr?.morning_minute_effective);
    const computed = computeStreak([...serverDates, ...queueDates], todayYmd);
    streakN = computed.streak;
    bestStreak = Math.max(serverBestStreak, computed.best);
  }

  // QUEUE-UI-B, Шаг 3: кольцо «Твои искорки» (XpRing, xp-поле снапшота) —
  // при непустой очереди прибавляем сумму xp её записей поверх серверного
  // значения, при пустой очереди — ровно xp с сервера, как было. null в
  // отдельной записи трактуем как 0 в этой сумме (запись просто не вносит
  // известный вклад в прогноз кольца) — отдельно от бейджа ниже, где та же
  // неизвестность делает расчёт целиком ненадёжным и глушит выдачу.
  let xpDisplay = xp;
  if (pendingQueue.length > 0) {
    const queueXpSum = pendingQueue.reduce((sum, e) => sum + (e.xp ?? 0), 0);
    xpDisplay = xp + queueXpSum;
  }

  const queuedDaySet = useMemo(
    () => new Set(pendingQueue.map((e) => e.dayNumber)),
    [pendingQueue]
  );

  const completedDays = useMemo(
    () => new Set(state.days.list.map((d) => d.day_number)),
    [state.days.list]
  );
  // OF-PKG1A: в день без задания dashboard_text не показывается (решение
  // владельца 04.10.2026) — даже если в state ещё лежит контент прошлого дня.
  const dayText = isRestDay ? null : (state.content?.dashboard_text ?? null);
  const badges = state.badges;
  const badgeAwards = state.awards;

  const isPrestart = dayN === 0;
  const dayLabel = isPrestart ? "Предстарт" : `День ${dayN}`;
  const heroTitle = courseTitle ? `${courseTitle} • ${dayLabel}` : dayLabel;

  // автоскрытие тоста через 10 сек
  useEffect(() => {
    if (recordToast === null) return;
    const t = setTimeout(() => setRecordToast(null), 10000);
    return () => clearTimeout(t);
  }, [recordToast]);

  // авто-рефетч при наступлении следующего окна
  // M5-STEP3B: раньше при отказе стирал живые данные (контент → null, дни →
  // пустой список) и молча выдавал вчерашний снапшот за сегодняшний. Теперь
  // при unavailable ничего не диспатчим — прежнее состояние остаётся на
  // экране (индикатор синхронизации отложен решением владельца), но отказ
  // различим в логе.
  const refetchAll = async () => {
    try {
      const snapResult = await getSnapshot();
      // M5-STORE1: "cached" наравне с "ok" — та же логика, что в init-эффекте.
      const snap =
        snapResult.status === "ok" || snapResult.status === "cached" ? snapResult.data : null;
      if (snapResult.status === "unavailable") {
        console.error("[refetch] snapshot unavailable");
      }
      if (snap) dispatch({ type: "SNAPSHOT_SET", snapshot: snap });

      const e = snap ? findEnrollment(snap, courseId) : null;
      // OF-PKG1A: тот же выбор дня, что в init-эффекте и в рендере.
      const newDay = contentDayFor(
        resolveEnrollmentToday(
          e,
          snap?.profile?.timezone ?? "Europe/Moscow",
          Number(settingsRef.current?.duration_days ?? 0)
        )
      );

      const freshDays = await getDays(courseId);
      if (freshDays.ok) {
        dispatch({ type: "DAYS_SET", days: freshDays });
      } else {
        console.error("[refetch] days fetch failed");
      }

      if (newDay > 0) {
        const c = await getContentDay(courseId, newDay);
        if (c.status === "unavailable") {
          console.error("[refetch] content day fetch failed");
        } else {
          dispatch({ type: "CONTENT_SET", content: c.day, media: c.media });
        }
      } else {
        dispatch({ type: "CONTENT_SET", content: null, media: EMPTY_MEDIA });
      }
    } catch (e) {
      console.error("[refetch] exception:", e instanceof Error ? e.message : String(e));
    }
  };

  // QUEUE-UI-A: queue:synced (после доставки хотя бы одной записи и свежего
  // снапшота в кэше, utils/queue.ts) — экран принимает серверные значения,
  // прогноз уходит. refetchAll обновляет снапшот/дни/контент дня тем же
  // путём, что и Countdown.onReached; награды перечитываются тем же путём,
  // что после онлайн-отметки (onCompletePractice, awardedNow-ветка).
  useEffect(() => {
    const onSynced = () => {
      void (async () => {
        await refetchAll();
        const freshAwardedResult = await loadAwarded(courseId);
        if (freshAwardedResult.status === "unavailable") {
          console.error("[dashboard-queue] awarded refresh after sync failed");
        } else {
          dispatch({ type: "AWARDS_SET", awards: freshAwardedResult.data });
        }
        // QUEUE-UI-B, Шаг 2: доставленная запись больше не шлёт queue:changed
        // сама (queue.ts — закрытие окна отката); pendingQueue перечитывается
        // здесь, ПОСЛЕ того как snapshot/days выше уже свежие — doneToday
        // остаётся true весь переход (state.days.todayCompleted уже true до
        // того, как очередь опустеет в этом же состоянии), откат не возникает.
        const freshPending = await getPending(courseId);
        setPendingQueue(freshPending);
      })();
    };
    window.addEventListener("queue:synced", onSynced);
    return () => window.removeEventListener("queue:synced", onSynced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // OF-PKG1A, шаг 4: переход утра на открытом экране — пересчитать дату на
  // экране и перечитать данные (офлайн refetchAll берёт снапшот из кэша, день
  // пересчитывается из schedule по nowTick).
  const onDayBoundary = () => {
    setNowTick(Date.now());
    void refetchAll();
  };

  // OF-PKG1A, шаг 4: возврат вкладки на экран и появление сети — то же
  // самое, не чаще раза в 30 с (отсечка по времени, без таймеров в фоне).
  // refetchAll из первого рендера безопасен: он читает только courseId,
  // dispatch и settingsRef (тот же приём, что queue:synced выше).
  useEffect(() => {
    const wake = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastWakeRef.current < 30_000) return;
      lastWakeRef.current = now;
      setNowTick(now);
      void refetchAll();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const artifactsByDay = useMemo<Map<number, Artifact>>(() => {
    if (!artifacts) return new Map();
    return new Map(artifacts.map((a) => [a.day_number, a]));
  }, [artifacts]);

  // M5-STEP3A: ошибка приоритетнее загрузки — раньше state.loading || !settings
  // держало экран на «Загрузка…» даже при взведённом error, потому что
  // setSettings стоит после проверок и при INIT_FAIL не выполняется.
  if (state.error) {
    return <main className="min-h-screen flex items-center justify-center">Ошибка: {state.error}</main>;
  }
  if (state.loading || !settings) {
    return <main className="min-h-screen flex items-center justify-center">Загрузка…</main>;
  }

  const target = nextTargetFor(xp, badges.map((b) => b.threshold));

  // OF-PKG1A: день без задания — без баннера и опроса; «нужна связь» — без
  // баннера (его кнопка открывает практику, которой нет).
  const showNudge = !isPrestart && !isRestDay && !isBeyond && !doneToday && isBetweenTZ(tz, 11, 13);
  const showEveningSurvey = !isPrestart && !isRestDay && doneToday && isAfterTZ(tz, 18, 0);

  // OF-PKG1A: утренний час — из enrollment'а (то же, по чему сервер считает
  // день), настройки курса — только если полей нет.
  const morningHour = enr?.morning_hour_effective ?? settings.morning_hour;
  const morningMinute = enr?.morning_minute_effective ?? settings.morning_minute;

  const startPractice = () => {
    logEvent("dashboard_start_practice_click", {
      client_id: cid,
      user_id: state.snapshot?.user_id ?? null,
      course_id: courseId,
    });
    setPane("practice");
  };

  // логика, которая должна выполняться ТОЛЬКО ПОСЛЕ закрытия окна «congrats»
  const onCompletePractice = async () => {
    // M5-QUEUE (20.09.2026): время фиксируется здесь, ОДИН раз, до любых
    // await — не в момент фактической отправки. Это тот же completed_at,
    // что уйдёт в очередь при отказе (см. ниже); повторная генерация времени
    // при отправке засчитала бы офлайн-отметку не за тот день (QUEUE-RECON,
    // Шаг 3; api/index.js:1409-1411 принимает прошлое как есть).
    const completedAtIso = new Date().toISOString();
    setCompleteError(false);
    setCompleteQueued(false);
    await logEvent("complete_practice", {
      client_id: cid,
      user_id: state.snapshot?.user_id ?? null,
      course_id: courseId,
      day_number: dayN,
    });

    const prevBest = bestStreak;
    const prevAwardCodes = new Set(state.awards.map((a) => a.code));
    const prevDayNumbers = new Set((state.days?.list ?? []).map((d) => Number(d.day_number)));
    const prevSparks = sumSparks(state.snapshot);

    const res = await completeDay(courseId, completedAtIso);
    if (!res.ok) {
      // M5-QUEUE: сеть недоступна или сервер временно недоступен (5xx) —
      // отложить в очередь с уже зафиксированным временем, а не терять
      // отметку. Терминальный отказ (400/403/404 — грейс истёк, курс не
      // активен, чужой курс и т.п.) в очередь не идёт: повтор того же
      // completed_at не изменит исход, это как прежде — обычная ошибка.
      const status = res.status;
      const isRetryable = !status || status >= 500;
      const userId = state.snapshot?.user_id;
      if (isRetryable && userId != null) {
        const queuedEntry = await enqueueOfflineCompletion({
          courseId,
          completedAt: completedAtIso,
          dayNumber: dayN,
          userId,
        });
        setCompleteQueued(true);

        // QUEUE-UI-B, Шаг 3/4/5: тот же путь, что онлайн-ветка ниже
        // (awardedNow), посчитанный локально — сервер подтвердит его при
        // фактической доставке, queue:synced выше перечитывает snapshot/
        // /awards с сервера и любой неверный прогноз поправляется. content-
        // access, инвариант 2: всё это — прогноз для отображения, не
        // решение о праве; право по-прежнему проверяется заново на сервере
        // в момент доставки (flushQueue → тот же /api/progress/complete-day).
        if (queuedEntry.xp != null) {
          window.dispatchEvent(new CustomEvent("wallet:earned", { detail: { amount: queuedEntry.xp } }));
          pendingSparksRef.current = queuedEntry.xp;

          // Бейдж — серверное правило дословно (Шаг 4): xpBefore = снапшот
          // + сумма xp прочих записей очереди этого курса (без текущей),
          // xpAfter = xpBefore + xp текущей. Любая запись очереди (кроме
          // текущей — её саму уже проверили выше) с xp=null делает сумму
          // ненадёжной — бейдж не выдаём, только логируем.
          const otherEntries = pendingQueue.filter((e) => e.id !== queuedEntry.id);
          const hasUnknownXp = otherEntries.some((e) => e.xp == null);
          if (hasUnknownXp) {
            console.error(
              `[dashboard-queue] badge forecast skipped: unreliable xp sum in queue, course=${courseId} day=${dayN}`
            );
          } else {
            const xpBefore = xp + otherEntries.reduce((sum, e) => sum + (e.xp ?? 0), 0);
            const xpAfter = xpBefore + queuedEntry.xp;
            const qualifying = qualifyingBadgeFrom(badges, xpBefore, xpAfter, dayN);
            if (qualifying && !state.awards.some((a) => a.code === qualifying.code)) {
              pendingBadgeRef.current = { code: qualifying.code, label: qualifying.label };
              void logEvent("badge_modal_queued", {
                client_id: cid,
                user_id: userId,
                course_id: courseId,
                badge_code: qualifying.code,
              });
            }
          }
        }

        // Модалка артефакта — тот же путь, что онлайн, но только если
        // артефакт дня уже в кэше (Шаг 5: нет артефакта — модалку не
        // открываем, остальная имитация работает как обычно). При закрытии
        // handleArtifactClose сам запускает анимацию монет и открывает
        // модалку бейджа, если та спрогнозирована выше.
        if (artifactsByDay.has(dayN)) {
          setArtifactIndex(dayN > 0 ? dayN : 1);
          setArtifactMode("award");
          setLockedReason(null);
          setArtifactOpen(true);
        } else {
          // Артефакта нет — handleArtifactClose не вызовется (модалка
          // артефакта не открылась): сбрасываем pendingSparksRef сами
          // (иначе следующее реальное открытие/закрытие артефакта унесло бы
          // эту сумму и сыграло бы анимацию монет не за то событие), и
          // показываем бейдж напрямую, если он спрогнозирован выше.
          pendingSparksRef.current = 0;
          const pending = pendingBadgeRef.current;
          if (pending) {
            pendingBadgeRef.current = null;
            void (async () => {
              await scrollBadges();
              lastBadgeRef.current = pending;
              setBadgeModal(pending);
            })();
          }
        }
      } else {
        // userId неизвестен (нет живого снапшота) — без него нельзя
        // безопасно проверить изоляцию при доставке (content-access, §9),
        // показываем обычный отказ вместо тихой постановки в очередь.
        setCompleteError(true);
      }
      void logEvent("complete_day_failed", {
        client_id: cid, course_id: courseId, day_number: dayN, reason: res.error,
      });
      return;
    }
    const fresh = res.snapshot;
    if (fresh) dispatch({ type: "SNAPSHOT_SET", snapshot: fresh });

    const freshDays = await getDays(courseId);
    dispatch({ type: "DAYS_SET", days: freshDays });

    const freshEnr = fresh ? findEnrollment(fresh, courseId) : null;
    const newBest = freshEnr?.best_streak ?? prevBest;

    const freshDayNumbers = new Set((freshDays?.days ?? []).map((d) => Number(d.day_number)));
    const awardedNow = !prevDayNumbers.has(Number(dayN)) && freshDayNumbers.has(Number(dayN));
    const sparksGained = Math.max(0, sumSparks(fresh) - prevSparks);

    if (newBest > prevBest) {
      setRecordToast(newBest);
      logEvent("new_best_streak", {
        client_id: cid,
        user_id: state.snapshot?.user_id ?? null,
        course_id: courseId,
        best: newBest,
      });
    }

    // Артефакт — по факту появления дня в days[] (день зачтён впервые), независимо
    // от начислений. Искорки/анимация — отдельно, только если реально начислено > 0.
    if (awardedNow) {
      setArtifactIndex(dayN > 0 ? dayN : 1);
      setArtifactMode("award");
      setLockedReason(null);
      setArtifactOpen(true);

      pendingSparksRef.current = sparksGained;

      if (sparksGained > 0) {
        window.dispatchEvent(new CustomEvent("wallet:earned", { detail: { amount: sparksGained } }));
      }

      void logEvent("xp_awarded", {
        client_id: cid,
        user_id: state.snapshot?.user_id ?? null,
        course_id: courseId,
        gained: sparksGained,
        day_number: dayN,
      });

      // бейдж выдаёт сервер — клиент только перечитывает и показывает выданное
      const freshAwardedResult = await loadAwarded(courseId);
      if (freshAwardedResult.status === "unavailable") {
        // M5-STEP3B: не удалось спросить — не подставляем данные, которых нет,
        // не трогаем state.awards. Модалка нового бейджа в этот раз не покажется.
        console.error("[dashboard-complete] awarded refresh failed");
      } else {
        const freshAwards = freshAwardedResult.data;
        dispatch({ type: "AWARDS_SET", awards: freshAwards });

        const newly = freshAwards.find((a) => !prevAwardCodes.has(a.code));
        if (newly) {
          pendingBadgeRef.current = {
            code: newly.code,
            label: newly.label || (state.badges.find((b) => b.code === newly.code)?.label ?? ""),
          };

          void logEvent("badge_modal_queued", {
            client_id: cid,
            user_id: state.snapshot?.user_id ?? null,
            course_id: courseId,
            badge_code: newly.code,
          });
        }
      }
    }
  };

  const onPracticeDoneShowCongrats = (html?: string | null) => {
    setPane("dashboard");
    setCongratsHtml(html ?? null);
  };

  const handleCongratsClose = async () => {
    setCongratsHtml(null);
    await onCompletePractice();
  };

  const handleArtifactClose = () => {
    setArtifactOpen(false);
    if (artifactMode === "award" && pendingSparksRef.current > 0) {
      setFlyKey((k) => k + 1);
    }
    pendingSparksRef.current = 0;

    const pending = pendingBadgeRef.current;
    if (pending) {
      pendingBadgeRef.current = null;
      (async () => {
        await scrollBadges();
        lastBadgeRef.current = pending;
        setBadgeModal(pending);
      })();
    }
  };

  const handleBadgeModalClose = async () => {
    const code = badgeModal?.code;
    const label = badgeModal?.label;

    setBadgeModal(null);

    const last = lastBadgeRef.current;
    if (last?.code) {
      requestAnimationFrame(() => {
        setZoomBadgeCode(last.code!);
        setZoomOpen(true);
        requestAnimationFrame(() => setZoomOpen(false));
      });
    }

    if (code && label && !state.awards.some((a) => a.code === code)) {
      dispatch({
        type: "AWARDS_SET",
        awards: [
          ...state.awards,
          { code: code as any, label, awardedAt: new Date().toISOString() } as BadgeAward,
        ],
      });
    }
  };

  const handleBadgeModalAfterClose = async () => {
    const FLY_MS = 600;
    setTimeout(() => setBadgesPulseKey((k) => k + 1), FLY_MS + 120);
    setTimeout(async () => {
      const result = await loadAwarded(courseId);
      if (result.status === "unavailable") {
        console.error("[dashboard-complete] awarded refresh after badge modal failed");
        return;
      }
      dispatch({ type: "AWARDS_SET", awards: result.data });
    }, FLY_MS + 180);
  };

  return (
    <main className="min-h-screen bg-transparent">
      {/* ======= Фоновая «шапка» с градиентом ======= */}
      <section
        className="rounded-b-none overflow-visible pb-7 md:pb-12"
        style={{
          backgroundImage: courseDesign?.dashboard_background_image_url
            ? `url("${courseDesign.dashboard_background_image_url}")`
            : undefined,
          backgroundColor: courseDesign?.dashboard_background_image_url
            ? undefined
            : (courseDesign?.course_background_color || 'var(--color-course-background)'),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderBottomLeftRadius: "60% 8%",
          borderBottomRightRadius: "60% 8%",
        }}
      >
        {/* === Артефакты — сразу под TopBar === */}
        <div className="max-w-5xl mx-auto px-4 pt-4">
          {/* QUEUE-UI-B, Шаг 6: плашка отклонённых записей очереди этого
              курса — постоянная, закрывается только человеком (не тост,
              разметка по образцу NudgeBanner). */}
          {rejectedEntries.length > 0 && (
            <div className="mb-4 rounded-2xl border border-rose-500/20 bg-rose-500/10 p-3 flex items-start justify-between gap-3">
              <div className="space-y-1">
                {rejectedEntries.map((e) => (
                  <div key={e.id} className="text-sm">
                    {rejectionMessage(e.dayNumber, e.reason)}
                  </div>
                ))}
              </div>
              <button
                aria-label="Закрыть уведомление"
                onClick={async () => {
                  await clearRejectedForCourse(courseId);
                  setRejectedEntries([]);
                }}
                className="h-7 w-7 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center shrink-0"
              >
                ×
              </button>
            </div>
          )}

          <ArtifactRail
            total={settings.duration_days}
            day={dayN}
            doneToday={doneToday}
            onArtifactClick={(d) => {
              if (artifacts === null) return;
              const isCollected = completedDays.has(d);
              let mode: ArtifactMode = "locked";
              let reason: LockedReason = "missed";

              if (isCollected) {
                mode = "preview";
                reason = null;
              } else if (d > dayN) {
                mode = "locked";
                reason = "future";
              } else if (d === dayN && !doneToday) {
                mode = "locked";
                reason = "today";
              } else {
                mode = "locked";
                reason = "missed";
              }

              setArtifactIndex(d);
              setArtifactMode(mode);
              setLockedReason(reason);
              setArtifactOpen(true);

              logEvent("artifact_day_open", {
                client_id: cid,
                user_id: state.snapshot?.user_id ?? null,
                course_id: courseId,
                day: d,
                mode,
                reason,
              });
            }}
            highlightIndex={artifactIndex ?? null}
            pulseKey={flyKey}
            artifactsByDay={artifactsByDay}
            queuedDays={queuedDaySet}
          />
        </div>

        {/* Hero */}
        <div className="max-w-5xl mx-auto px-4 pt-6">
          <div className="rounded-3xl bg-transparent border-0 shadow-none p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-semibold text-center">{heroTitle}</h1>

                {dayText && dayText.trim() && (
                  <div
                    className="opacity-80 mt-1 text-center"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dayText) }}
                  />
                )}
              </div>
            </div>

            {isBeyond ? (
              <div className="mt-5 text-center text-sm opacity-80">
                Нет связи больше трёх дней. Подключись к интернету, чтобы открыть новые задания.
              </div>
            ) : isRestDay ? (
              <div className="mt-5 flex flex-col items-center gap-3">
                <div className="text-center">Сегодня задания нет — день отдыха.</div>
                <Countdown
                  timezone={tz}
                  targetHour={morningHour}
                  targetMinute={morningMinute}
                  label="До следующего задания:"
                  variant="pill"
                  onReached={onDayBoundary}
                />
              </div>
            ) : isPrestart || doneToday ? (
              <div className="mt-5 flex justify-center">
                <Countdown
                  timezone={tz}
                  targetHour={morningHour}
                  targetMinute={morningMinute}
                  label={isPrestart ? "До старта:" : "До следующего задания:"}
                  variant="pill"
                  onReached={onDayBoundary}
                />
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center gap-3">
                <div className="text-xs opacity-60">~3 мин</div>
                <button
                  onClick={startPractice}
                  className="btn-primary px-6 py-3 rounded-2xl text-base font-normal
                             shadow-lg"
                >
                  Начать практику
                </button>
              </div>
            )}
          </div>

          <NudgeBanner show={showNudge} onAction={startPractice} text="3 минуты — успеешь до обеда 😉" />
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-5xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <XpRing xp={xpDisplay} target={target} />
          </div>

          <div>
            <StreakBar
              streak={streakN}
              timezone={tz}
              percentToday={dayProgressPercent(tz)}
              bestStreak={bestStreak}
            />
          </div>

          <div
            id="badges-tile"
            ref={badgesTileRef}
            className={[
              "rounded-2xl bg-white border border-gray-200 shadow-sm p-4",
              badgesPulseKey > 0 ? "animate-[arcGlow_600ms_ease-out]" : "",
            ].join(" ")}
          >
            <div className="text-sm font-semibold mb-2">Мои достижения</div>
            {badgesUnavailable ? (
              <div className="text-sm text-red-600">Не удалось загрузить награды</div>
            ) : (
            <BadgeStrip
              badges={badges}
              xp={xp}
              day={dayN}
              awards={badgeAwards}
              onBadgeClick={(b) => {
                // базовый лог клика по бейджу
                logEvent("badge_click", {
                  client_id: cid,
                  user_id: state.snapshot?.user_id ?? null,
                  course_id: courseId,
                  badge_code: b.code,
                });

                // если модалка уже открыта — игнорируем повторные клики
                if (badgeModal) return;

                // открываем ту же модалку "Новое достижение"
                lastBadgeRef.current = { code: b.code, label: b.label };
                setBadgeModal({ code: b.code, label: b.label });

                // телеметрия открытия модалки по клику
                logEvent("badge_modal_open_click", {
                  client_id: cid,
                  user_id: state.snapshot?.user_id ?? null,
                  course_id: courseId,
                  badge_code: b.code,
                  source: "dashboard_strip",
                });
              }}
            />
            )}
          </div>

          <style>{`
            @keyframes arcGlow {
              0% { box-shadow: 0 0 0 0 rgba(251,191,36,0); transform: scale(1); }
              40% { box-shadow: 0 0 0 10px rgba(251,191,36,.25); transform: scale(1.02); }
              100% { box-shadow: 0 0 0 0 rgba(251,191,36,0); transform: scale(1); }
            }
          `}</style>
        </div>

        {showEveningSurvey && (
          <div className="mt-4">
            <EveningSurvey
              show={true}
              onSubmit={(s) => logEvent("energy_score", { client_id: cid, user_id: state.snapshot?.user_id ?? null, s })}
            />
          </div>
        )}

        {/* Оставляем только одну кнопку — Поддержка */}
        <div className="grid grid-cols-1 gap-4 mt-6">
          <Tile
            label="Поддержка"
            onClick={() => {
              logEvent("dashboard_support_click", {
                client_id: cid,
                user_id: state.snapshot?.user_id ?? null,
                course_id: courseId,
              });
              nav("/support");
            }}
          />
        </div>
      </section>

      {recordToast !== null && (
        <div className="fixed bottom-6 inset-x-0 z-[60] flex justify-center px-4">
          <div
            role="status"
            aria-live="polite"
            className="relative max-w-md w/full rounded-2xl bg-emerald-600 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] px-5 py-4
                 flex items-start gap-3"
          >
            <div className="text-2xl leading-none">🎉</div>
            <div className="flex-1">
              <div className="text-base font-bold">Новый рекорд!</div>
              <div className="text-sm/5 opacity-95">
                Серия достигла <span className="font-semibold">{recordToast}</span> дней.
              </div>
            </div>

            <button
              aria-label="Закрыть уведомление"
              onClick={() => setRecordToast(null)}
              className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {(completeError || completeQueued) && (
        <div className="fixed bottom-6 inset-x-0 z-[60] flex justify-center px-4">
          <div role="status" aria-live="polite"
               className="relative max-w-md w-full rounded-2xl bg-rose-600 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] px-5 py-4 flex items-start gap-3">
            <div className="text-2xl leading-none">⚠️</div>
            <div className="flex-1">
              {completeQueued ? (
                <div className="text-base font-bold">Отметка сохранена и отправится, как только появится связь.</div>
              ) : (
                <>
                  <div className="text-base font-bold">Не удалось отметить день</div>
                  <div className="text-sm/5 opacity-95">Проверьте соединение и попробуйте ещё раз.</div>
                </>
              )}
            </div>
            <button aria-label="Закрыть уведомление" onClick={() => { setCompleteError(false); setCompleteQueued(false); }}
                    className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">×</button>
          </div>
        </div>
      )}

      {/* Модалка «Новое достижение» */}
      <Modal
        open={!!badgeModal}
        onClose={handleBadgeModalClose}
        onAfterClose={handleBadgeModalAfterClose}
        placement="center"
        height="screen80"
        maxWidth="max-w-md"
        closeOnOverlay={false}
        labelledBy="badge-modal-title"
      >
        {/* Колонка с ограничением высоты, как в ArtifactModal */}
        <div className="flex flex-col max-h-[80vh]">
          {/* Шапка + скроллимый контент */}
          <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4 text-center">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center text-white text-2xl shadow-lg">
              🏅
            </div>

            <h3 id="badge-modal-title" className="text-xl font-bold">Новое достижение!</h3>

            {badgeModal && (
              <div className="mt-0.5 text-sm text-gray-500">
                Ты получил(а) достижение <span className="font-semibold">{badgeModal.label}</span>.
              </div>
            )}

            {/* Большое превью изображения бейджа, если доступно в каталоге */}
            {badgeModal && (() => {
              const b = badges.find((bd) => String(bd.code) === String(badgeModal.code));
              const imgId = b?.image;
              if (!imgId) return null;
              const src = `${API_BASE}/assets/${imgId}`;
              return (
                <div className="relative mt-4 w-full rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <img
                    src={src}
                    alt={badgeModal.label}
                    className="w-full h-auto max-h-[56vh] object-contain select-none"
                    loading="eager"
                    decoding="async"
                    draggable={false}
                  />
                </div>
              );
            })()}
          </div>

          {/* Футер c CTA — всегда виден */}
          <div className="px-5 pt-3 pb-4 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <div className="flex items-center justify-center">
              <button
                onClick={handleBadgeModalClose}
                className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
              >
                Класс!
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* NEW: модалка «Отлично!» (дизайн как у ActivationCodeModal) */}
      <Modal open={!!congratsHtml} onClose={handleCongratsClose} maxWidth="max-w-[560px]">
        <div
          className="
            mx-auto max-w-[560px] w-full
            rounded-3xl bg-white dark:bg-slate-900
            shadow-xl ring-1 ring-slate-200 dark:ring-slate-800
            p-6
          "
        >
          {/* Заголовок + сабтекст (8–16–24) */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <svg
                aria-hidden="true"
                className="h-5 w-5 text-slate-600 dark:text-slate-300"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M9 16.2 5.5 12.7l1.4-1.4L9 13.4l8.1-8.1 1.4 1.4L9 16.2Z" />
              </svg>
              <h2 className="text-[22px] leading-7 font-semibold text-slate-900 dark:text-slate-100">
                Отлично!
              </h2>
            </div>
            <p className="text-center text-sm text-slate-600 dark:text-slate-300">
              {/* ниже — контент дня из Directus */}
            </p>
          </div>

          <div
            className="text-sm text-slate-700 dark:text-slate-300 text-center"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(congratsHtml || "Практика отмечена как выполненная."),
            }}
          />

          <button
            onClick={handleCongratsClose}
            className="
              mt-4 w-full rounded-2xl py-3 font-semibold text-white
              bg-emerald-600 hover:bg-emerald-700
              focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2
            "
          >
            Продолжить
          </button>
        </div>
      </Modal>

     {/* NEW: модалка артефакта */}
      <ArtifactModal
        open={artifactOpen}
        day={artifactIndex ?? dayN}
        total={settings.duration_days}
        // @ts-ignore
        mode={artifactMode}
        // @ts-ignore
        lockedReason={lockedReason ?? undefined}
        onClose={handleArtifactClose}
        artifact={artifacts?.find((a) => a.day_number === (artifactIndex ?? dayN)) ?? null}
      />

      <BadgeZoomOverlay open={zoomOpen} code={zoomBadgeCode} label={badgeModal?.label ?? null} />

      {flyKey > 0 && <SparksFly key={flyKey} targetSelector="#wallet-pill" count={10} size={22} durationMs={880} spinDeg={270} arc={0.4} />}

      {pane === "practice" && (
        <PracticePane
          day={dayN}
          content={state.content}
          media={state.media}
          loading={state.loading}
          err={state.error}
          onClose={() => setPane("dashboard")}
          onComplete={onPracticeDoneShowCongrats}
        />
      )}
    </main>
  );
}

function Tile({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="h-28 rounded-2xl bg-white border border-gray-200 shadow-sm
                 hover:shadow-md transition flex items-center justify-center text-sm font-semibold"
    >
      {label}
    </button>
  );
}

/** Встроенная панель практики (локальный компонент) — контент/медиа приходят от родителя (кэш в reducer) */
function PracticePane({
  day,
  content,
  media,
  loading,
  err,
  onClose,
  onComplete,
}: {
  day: number;
  content: DayContent | null;
  media: DayMedia;
  loading: boolean;
  err: string | null;
  onClose: () => void;
  onComplete: (congratsHtml?: string | null) => void;
}) {
  const handleComplete = () => {
    onComplete(content?.congrats_text ?? null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/10 backdrop-blur-sm">
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="md:max-w-2xl max-h-[85vh] md:max-h-[85vh] overflow-auto mx-auto bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-6 md:p-8">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Практика — День {day}</h2>
            <button onClick={onClose} className="text-sm opacity-60 hover:opacity-100">
              Закрыть
            </button>
          </div>

          {loading && <div className="mt-4 opacity-70">Загрузка…</div>}
          {err && <div className="mt-4 text-red-600">{err}</div>}
          {!loading && !err && !content && (
            <div className="mt-4 text-red-600">
              {/* SW-PRELOAD (19.09.2026), Шаг 5: content.ts кладёт в кэш только
                  "ok" (легитимный ответ сервера) — "empty" никогда не пишется и
                  не читается из IndexedDB (комментарий в content.ts). Значит
                  офлайн + !content всегда значит "этот день не предзагружен",
                  никогда легитимно пустой текст (тот доступен только при живом
                  ответе сервера). При живой сети текст не меняется —
                  PracticePane по-прежнему не различает причину онлайн. */}
              {typeof navigator !== "undefined" && navigator.onLine === false
                ? "Курс ещё не загружен. Откройте его один раз при связи — дальше он будет работать без интернета."
                : "Задание на этот день пока не заведено."}
            </div>
          )}

          {!loading && !err && content && (
            <div className="space-y-5 mt-4">
              {content.title && <h3 className="text-lg font-semibold">{content.title}</h3>}

              {content.main_text && (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(content.main_text),
                  }}
                />
              )}

              {(media.audio_url || media.video_url) && (
                <div className="space-y-4">
                  {media.audio_url && (
                    <audio controls preload="metadata" className="w-full">
                      <source src={mediaUrl(media.audio_url) ?? ""} />
                    </audio>
                  )}
                  {media.video_url && (
                    <video
                      controls
                      preload="metadata"
                      className="w-full rounded-xl"
                      src={mediaUrl(media.video_url) ?? ""}
                    />
                  )}
                </div>
              )}

              {content.extra_tasks && (
                <div>
                  <div className="text-sm font-semibold mb-1">Доп. задания</div>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(content.extra_tasks),
                    }}
                  />
                </div>
              )}

              {content.special_instructions && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(content.special_instructions),
                    }}
                  />
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleComplete}
                  className="px-6 py-3 rounded-2xl font-semibold text-white bg-[#16a34a] hover:bg-[#15803d] active:bg-[#166534]"
                >
                  Готово
                </button>
                <button onClick={onClose} className="px-6 py-3 rounded-2xl font-semibold border border-gray-300">
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
